/**
 * A USB da tela de Ajustes (spec 2026-09-24-robo-painel-de-ajustes, 3.1, 3.2 e
 * 3.4): ao abrir, testa UMA vez as portas já autorizadas neste navegador e fica
 * com a que casa com o cadastro; mantém a porta aberta enquanto a tela existir;
 * e segue o cabo pelos eventos `connect`/`disconnect`.
 *
 * Nunca varre em segundo plano nem num intervalo: abrir a porta reinicia a
 * placa CP2102 do Robô I.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { lerRecursos, type InfoDoRobo, type Recursos } from '@/features/robo-usb/protocolo'
import {
  eDaFrota, ouvirPortas, pedirPorta, portaPresa, portasAutorizadasDoRobo, temWebSerial,
  type PortaSerial,
} from '@/features/robo-usb/serial'
import { vincularPlaca } from './canal'
import { abrirEIdentificar, embrulhar, type PortaDoRobo } from './porta'
import { BufferDeLog } from './redacao'

export type EstadoDaUsb =
  | { fase: 'sem_web_serial' }
  | { fase: 'procurando' }
  | { fase: 'conectada'; info: InfoDoRobo }
  /** Cadastro sem MAC e uma placa respondendo: perguntar se é este robô. */
  | { fase: 'confirmar'; mac: string }
  /** Cadastro sem MAC e mais de uma placa autorizada: a pessoa escolhe pelo seletor. */
  | { fase: 'escolher'; quantas: number }
  | { fase: 'ausente'; aviso: string | null }

/** Grava o MAC no cadastro; devolve o código da recusa, ou `null` quando gravou. */
export type Vincular = (mac: string) => Promise<string | null>

export const AVISO_DO_VINCULO: Record<string, string> = {
  placa_de_outro_robo: 'Esta placa está cadastrada em outro robô da frota. A porta foi fechada.',
  placa_diverge: 'O cadastro deste robô já tem outra placa. A porta foi fechada.',
}

const FIRMWARE_ANTIGO = 'O firmware desta placa é anterior à identificação pela USB: atualize o firmware para ajustar por aqui.'
const SEM_RESPOSTA = 'A placa não respondeu pela USB. Confira o cabo e tente de novo.'
const PORTA_PRESA = 'A porta USB ficou presa aberta, talvez em outra aba do Console. Feche as outras abas do Console ou recarregue esta página, e tente de novo.'

/** Linhas lidas antes de a porta ser escolhida: só entram no log se ela for a escolhida. */
const TETO_DAS_PREVIAS = 200

export function useCanalUsb({ macDoCadastro, vincular, nav = navigator }: {
  macDoCadastro: string | null
  vincular: Vincular
  nav?: Navigator
}) {
  const [estado, setEstado] = useState<EstadoDaUsb>(() =>
    (temWebSerial(nav) ? { fase: 'procurando' } : { fase: 'sem_web_serial' }))
  const buffer = useRef(new BufferDeLog())
  const [versaoDoLog, setVersaoDoLog] = useState(0)
  const [recursos, setRecursos] = useState<(Recursos & { em: number }) | null>(null)
  /** A porta escolhida (conectada) ou à espera de confirmação. */
  const atual = useRef<PortaDoRobo | null>(null)
  const escolhida = useRef(false)
  /** Toda porta aberta por esta tela, para fechar ao sair. */
  const abertas = useRef(new Set<PortaDoRobo>())
  const previas = useRef(new Map<PortaDoRobo, string[]>())
  const macRef = useRef(macDoCadastro)
  macRef.current = macDoCadastro
  /** Muda quando a tela sai: uma sondagem em curso de uma montagem anterior não escreve estado. */
  const geracao = useRef(0)

  const registrar = useCallback((linha: string) => {
    buffer.current.acrescentar(linha)
    const r = lerRecursos(linha)
    if (r) setRecursos({ ...r, em: Date.now() })
    setVersaoDoLog((v) => v + 1)
  }, [])

  const ouvir = useCallback((p: PortaDoRobo, linha: string) => {
    if (escolhida.current && atual.current === p) { registrar(linha); return }
    const lista = previas.current.get(p) ?? []
    lista.push(linha)
    if (lista.length > TETO_DAS_PREVIAS) lista.shift()
    previas.current.set(p, lista)
  }, [registrar])

  const identificar = useCallback(async (p: PortaDoRobo) => {
    abertas.current.add(p)
    return abrirEIdentificar(p, { aoLer: (l) => ouvir(p, l) })
  }, [ouvir])

  const fechar = useCallback(async (p: PortaDoRobo | null) => {
    if (!p) return
    abertas.current.delete(p)
    previas.current.delete(p)
    if (atual.current === p) { atual.current = null; escolhida.current = false }
    await p.fechar()
  }, [])

  const usar = useCallback((p: PortaDoRobo) => {
    atual.current = p
    escolhida.current = true
    for (const l of previas.current.get(p) ?? []) registrar(l)
    previas.current.delete(p)
    if (p.info) setEstado({ fase: 'conectada', info: p.info })
  }, [registrar])

  /** Passo 1 da spec 3.1 sobre estas portas. */
  const sondar = useCallback(async (portas: PortaSerial[]) => {
    const g = geracao.current
    setEstado({ fase: 'procurando' })
    const embrulhadas = portas.map(embrulhar)
    const v = await vincularPlaca(embrulhadas, macRef.current, identificar)
    for (const p of embrulhadas) {
      if (!(v.tipo === 'casou' && v.porta === p) && !(v.tipo === 'confirmar' && v.porta === p)) {
        abertas.current.delete(p)
        previas.current.delete(p)
      }
    }
    if (g !== geracao.current) {
      if (v.tipo === 'casou' || v.tipo === 'confirmar') await v.porta.fechar()
      return
    }
    if (v.tipo === 'casou') usar(v.porta)
    else if (v.tipo === 'confirmar') {
      atual.current = v.porta
      setEstado({ fase: 'confirmar', mac: v.mac })
    } else if (v.tipo === 'escolher') setEstado({ fase: 'escolher', quantas: v.portas.length })
    else {
      setEstado({ fase: 'ausente', aviso: embrulhadas.some((p) => p.firmwareAntigo) ? FIRMWARE_ANTIGO : null })
    }
  }, [identificar, usar])

  // Uma vez, ao abrir a tela; ao sair, fecha tudo o que abriu (a porta presa só
  // soltaria recarregando). No StrictMode a segunda montagem sonda de novo: a
  // primeira foi desfeita pela limpeza.
  useEffect(() => {
    const g = ++geracao.current
    if (temWebSerial(nav)) {
      void (async () => {
        const portas = await portasAutorizadasDoRobo(nav)
        if (g === geracao.current) await sondar(portas)
      })()
    }
    return () => {
      geracao.current++
      for (const p of abertas.current) void p.fechar()
      abertas.current.clear()
      previas.current.clear()
      atual.current = null
      escolhida.current = false
    }
  }, [nav, sondar])

  // O cabo (spec 3.4): saiu, a tela volta para a rede; voltou, testa SÓ aquela porta.
  useEffect(() => {
    if (!temWebSerial(nav)) return
    return ouvirPortas(
      (porta) => {
        if (escolhida.current || atual.current || !eDaFrota(porta)) return
        void sondar([porta])
      },
      (porta) => {
        const p = atual.current
        if (!p || p.porta !== porta) return
        void fechar(p)
        setEstado({ fase: 'ausente', aviso: 'USB desconectada.' })
      },
      nav,
    )
  }, [nav, sondar, fechar])

  /** "Conectar pela USB": o seletor do navegador. Tem de rodar dentro do clique. */
  const conectarPeloSeletor = useCallback(async () => {
    let porta: PortaSerial
    try {
      porta = await pedirPorta(nav)
    } catch {
      return // fechou o seletor sem escolher
    }
    const g = geracao.current
    setEstado({ fase: 'procurando' })
    const p = embrulhar(porta)
    let lida: { mac: string } | null = null
    let presa = false
    try {
      lida = await identificar(p)
    } catch (erro) {
      lida = null
      presa = portaPresa(erro)
    }
    if (g !== geracao.current) { await fechar(p); return }
    if (!lida) {
      const aviso = presa ? PORTA_PRESA : p.firmwareAntigo ? FIRMWARE_ANTIGO : SEM_RESPOSTA
      await fechar(p)
      setEstado({ fase: 'ausente', aviso })
      return
    }
    const cadastro = macRef.current
    if (!cadastro) {
      atual.current = p
      setEstado({ fase: 'confirmar', mac: lida.mac })
    } else if (cadastro.toLowerCase() === lida.mac.toLowerCase()) {
      usar(p)
    } else {
      await fechar(p)
      setEstado({
        fase: 'ausente',
        aviso: `A placa no cabo (${lida.mac}) não é a deste robô (${cadastro}). A porta foi fechada.`,
      })
    }
  }, [nav, identificar, fechar, usar])

  /** "Sim, é este robô": grava o MAC no cadastro, e só então a porta vira o canal. */
  const confirmar = useCallback(async () => {
    const p = atual.current
    if (!p?.info) return
    const recusa = await vincular(p.info.mac)
    if (recusa === null) { usar(p); return }
    await fechar(p)
    setEstado({ fase: 'ausente', aviso: AVISO_DO_VINCULO[recusa] ?? 'O Console não gravou a placa no cadastro. A porta foi fechada.' })
  }, [vincular, usar, fechar])

  const recusar = useCallback(async () => {
    await fechar(atual.current)
    setEstado({ fase: 'ausente', aviso: null })
  }, [fechar])

  return {
    estado,
    /** As linhas da porta escolhida, aberta agora — `null` fora da fase `conectada`. */
    linhas: estado.fase === 'conectada' ? atual.current?.linhas ?? null : null,
    log: buffer.current,
    versaoDoLog,
    recursos,
    conectarPeloSeletor,
    confirmar,
    recusar,
  }
}
